import React from "react";
import { Form, Button, TimePicker, DatePicker, Select, Space, Card, Divider, Tooltip, Radio, Alert } from "antd";
import { PlusOutlined, DeleteOutlined, InfoCircleOutlined, ClockCircleOutlined, GlobalOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTimeZone, TIMEZONE_OPTIONS } from "../../../../contexts/TimeZoneContext";
import { UTCTimePicker, UTCTimeRangePicker, UTCDateRangePicker } from "../../../../components/DateTimeInput";
import AircraftTag from "../../../../components/AircraftTag";
import AircraftSelector from "../../../../components/common/AircraftSelector";

const { Option } = Select;
const { RangePicker } = DatePicker;

// 星期选项
const WEEKDAY_OPTIONS = [
    { label: "周一", value: "MON" },
    { label: "周二", value: "TUE" },
    { label: "周三", value: "WED" },
    { label: "周四", value: "THU" },
    { label: "周五", value: "FRI" },
    { label: "周六", value: "SAT" },
    { label: "周日", value: "SUN" },
];

/**
 * 宵禁规则编辑器组件
 * @param {Object} props
 * @param {Array} props.depCurfewRules - 起飞宵禁规则数组
 * @param {Function} props.onDepCurfewRulesChange - 起飞宵禁规则变更回调
 * @param {Array} props.arrCurfewRules - 降落宵禁规则数组
 * @param {Function} props.onArrCurfewRulesChange - 降落宵禁规则变更回调
 */
const CurfewRulesEditor = ({ depCurfewRules = [], onDepCurfewRulesChange, arrCurfewRules = [], onArrCurfewRulesChange }) => {
    const { timeZone } = useTimeZone(); // 获取当前时区设置

    // 辅助函数：检测规则中是否包含非整点的时间
    const hasNonHourlyTimes = (rules) => {
        if (!rules || rules.length === 0) return false;
        return rules.some((rule) => {
            const checkMinutes = (timeStr) => {
                if (!timeStr) return false;
                const parts = timeStr.split(":");
                return parts.length === 2 && parts[1] !== "00";
            };
            return checkMinutes(rule.start) || checkMinutes(rule.end);
        });
    };

    // 格式化时间范围值
    const formatTimeRangeValue = (startStr, endStr) => {
        if (!startStr || !endStr) return [];
        try {
            // 更健壮的时间格式处理 - 强制使用UTC时区
            const parseTime = (timeStr) => {
                // 直接尝试使用dayjs.utc解析
                const parsed = dayjs.utc(timeStr, "HH:mm");
                if (parsed.isValid()) {
                    return parsed;
                }

                // 如果直接解析失败，尝试分割字符串并手动构建UTC时间
                try {
                    const [hours, minutes] = timeStr.split(":").map((num) => parseInt(num, 10));
                    if (!isNaN(hours) && !isNaN(minutes)) {
                        // 使用当前日期构建有效的UTC dayjs对象
                        return dayjs.utc().hour(hours).minute(minutes).second(0);
                    }
                } catch (err) {
                    console.error("时间格式解析失败:", timeStr, err);
                }

                // 如果都失败了，返回当前UTC时间作为fallback
                console.warn(`无法解析时间 "${timeStr}"，使用当前UTC时间代替`);
                return dayjs.utc();
            };

            return [parseTime(startStr), parseTime(endStr)];
        } catch (error) {
            console.error("时间格式解析错误:", error, "开始时间:", startStr, "结束时间:", endStr);
            return [];
        }
    };

    // 格式化日期范围值
    const formatDateRangeValue = (startStr, endStr) => {
        if (!startStr || !endStr) return [];
        try {
            // 使用UTC时区解析日期
            return [dayjs.utc(startStr), dayjs.utc(endStr)];
        } catch (error) {
            console.error("日期格式解析错误:", error);
            return [];
        }
    };

    // 添加起飞宵禁规则
    const addDepCurfewRule = () => {
        const newRules = [
            ...depCurfewRules,
            {
                type: "daily", // 默认为每天
                start: "22:00", // 确保使用标准格式
                end: "06:00", // 确保使用标准格式
                weekdays: [], // 星期几（针对weekly类型）
                date_start: null, // 具体开始日期（针对specific类型）
                date_end: null, // 具体结束日期（针对specific类型）
                valid_from: null, // 有效期开始日期（针对weekly类型）
                valid_to: null, // 有效期结束日期（针对weekly类型）
                exceptions: [],
            },
        ];
        onDepCurfewRulesChange(newRules);
    };

    // 添加降落宵禁规则
    const addArrCurfewRule = () => {
        const newRules = [
            ...arrCurfewRules,
            {
                type: "daily", // 默认为每天
                start: "23:00", // 确保使用标准格式
                end: "05:30", // 确保使用标准格式
                weekdays: [], // 星期几（针对weekly类型）
                date_start: null, // 具体开始日期（针对specific类型）
                date_end: null, // 具体结束日期（针对specific类型）
                valid_from: null, // 有效期开始日期（针对weekly类型）
                valid_to: null, // 有效期结束日期（针对weekly类型）
                exceptions: [],
            },
        ];
        onArrCurfewRulesChange(newRules);
    };

    // 删除起飞宵禁规则
    const removeDepCurfewRule = (index) => {
        const newRules = [...depCurfewRules];
        newRules.splice(index, 1);
        onDepCurfewRulesChange(newRules);
    };

    // 删除降落宵禁规则
    const removeArrCurfewRule = (index) => {
        const newRules = [...arrCurfewRules];
        newRules.splice(index, 1);
        onArrCurfewRulesChange(newRules);
    };

    // 更新起飞宵禁规则时间范围
    const updateDepCurfewRuleTimeRange = (index, times) => {
        const newRules = [...depCurfewRules];
        if (times && times.length === 2) {
            try {
                // 确保两个时间都是有效的
                if (times[0].isValid() && times[1].isValid()) {
                    // 统一格式为HH:mm - 使用UTC时间
                    newRules[index].start = times[0].utc().format("HH:mm");
                    newRules[index].end = times[1].utc().format("HH:mm");
                } else {
                    console.error("起飞宵禁规则时间无效:", times);
                }
            } catch (error) {
                console.error("更新起飞宵禁规则时间错误:", error);
            }
        }
        onDepCurfewRulesChange(newRules);
    };

    // 更新降落宵禁规则时间范围
    const updateArrCurfewRuleTimeRange = (index, times) => {
        const newRules = [...arrCurfewRules];
        if (times && times.length === 2) {
            try {
                // 确保两个时间都是有效的
                if (times[0].isValid() && times[1].isValid()) {
                    // 统一格式为HH:mm - 使用UTC时间
                    newRules[index].start = times[0].utc().format("HH:mm");
                    newRules[index].end = times[1].utc().format("HH:mm");
                } else {
                    console.error("降落宵禁规则时间无效:", times);
                }
            } catch (error) {
                console.error("更新降落宵禁规则时间错误:", error);
            }
        }
        onArrCurfewRulesChange(newRules);
    };

    // 更新起飞宵禁规则日期范围
    const updateDepCurfewRuleDateRange = (index, dates) => {
        const newRules = [...depCurfewRules];
        if (dates && dates.length === 2) {
            // 使用UTC时区格式化日期
            newRules[index].date_start = dates[0].utc().format("YYYY-MM-DD");
            newRules[index].date_end = dates[1].utc().format("YYYY-MM-DD");
        } else {
            newRules[index].date_start = null;
            newRules[index].date_end = null;
        }
        onDepCurfewRulesChange(newRules);
    };

    // 更新降落宵禁规则日期范围
    const updateArrCurfewRuleDateRange = (index, dates) => {
        const newRules = [...arrCurfewRules];
        if (dates && dates.length === 2) {
            // 使用UTC时区格式化日期
            newRules[index].date_start = dates[0].utc().format("YYYY-MM-DD");
            newRules[index].date_end = dates[1].utc().format("YYYY-MM-DD");
        } else {
            newRules[index].date_start = null;
            newRules[index].date_end = null;
        }
        onArrCurfewRulesChange(newRules);
    };

    // 更新起飞宵禁规则类型
    const updateDepCurfewRuleType = (index, type) => {
        const newRules = [...depCurfewRules];
        newRules[index].type = type;
        onDepCurfewRulesChange(newRules);
    };

    // 更新降落宵禁规则类型
    const updateArrCurfewRuleType = (index, type) => {
        const newRules = [...arrCurfewRules];
        newRules[index].type = type;
        onArrCurfewRulesChange(newRules);
    };

    // 更新起飞宵禁规则星期几
    const updateDepCurfewRuleWeekdays = (index, weekdays) => {
        const newRules = [...depCurfewRules];
        newRules[index].weekdays = weekdays;
        onDepCurfewRulesChange(newRules);
    };

    // 更新降落宵禁规则星期几
    const updateArrCurfewRuleWeekdays = (index, weekdays) => {
        const newRules = [...arrCurfewRules];
        newRules[index].weekdays = weekdays;
        onArrCurfewRulesChange(newRules);
    };

    // 更新起飞宵禁规则例外飞机
    const updateDepCurfewRuleExceptions = (index, exceptions) => {
        const newRules = [...depCurfewRules];
        newRules[index].exceptions = exceptions;
        onDepCurfewRulesChange(newRules);
    };

    // 更新降落宵禁规则例外飞机
    const updateArrCurfewRuleExceptions = (index, exceptions) => {
        const newRules = [...arrCurfewRules];
        newRules[index].exceptions = exceptions;
        onArrCurfewRulesChange(newRules);
    };

    // 格式化有效期范围值
    const formatValidityRangeValue = (fromStr, toStr) => {
        if (!fromStr || !toStr) return [];
        try {
            // 使用UTC时区解析日期
            return [dayjs.utc(fromStr), dayjs.utc(toStr)];
        } catch (error) {
            console.error("有效期格式解析错误:", error);
            return [];
        }
    };

    // 更新起飞宵禁规则有效期范围
    const updateDepCurfewRuleValidityRange = (index, dates) => {
        const newRules = [...depCurfewRules];
        if (dates && dates.length === 2) {
            // 使用UTC时区格式化日期
            newRules[index].valid_from = dates[0].utc().format("YYYY-MM-DD");
            newRules[index].valid_to = dates[1].utc().format("YYYY-MM-DD");
        } else {
            newRules[index].valid_from = null;
            newRules[index].valid_to = null;
        }
        onDepCurfewRulesChange(newRules);
    };

    // 更新降落宵禁规则有效期范围
    const updateArrCurfewRuleValidityRange = (index, dates) => {
        const newRules = [...arrCurfewRules];
        if (dates && dates.length === 2) {
            // 使用UTC时区格式化日期
            newRules[index].valid_from = dates[0].utc().format("YYYY-MM-DD");
            newRules[index].valid_to = dates[1].utc().format("YYYY-MM-DD");
        } else {
            newRules[index].valid_from = null;
            newRules[index].valid_to = null;
        }
        onArrCurfewRulesChange(newRules);
    };

    return (
        <div className="curfew-rules__editor">
            {/* 起飞宵禁规则 */}
            <Card
                title={
                    <span className="curfew-rules__title">
                        <ClockCircleOutlined style={{ color: "#f5222d" }} />
                        起飞宵禁规则
                        <Tooltip title="在设定的时间段内，所有飞机不允许从该机场起飞，除非在例外列表中">
                            <InfoCircleOutlined />
                        </Tooltip>
                    </span>
                }
                extra={
                    <Button type="primary" size="small" icon={<PlusOutlined />} onClick={addDepCurfewRule}>
                        添加规则
                    </Button>
                }
                bordered={false}
                size="small"
            >
                {hasNonHourlyTimes(depCurfewRules) && <Alert message="时间设置建议" description="检测到您设置的结束时间不是整点（例如 08:30 或 00:00）。建议使用整点时间（例如 08:00 或 23:00）以确保宵禁规则的清晰。系统允许在宵禁结束时点起飞或降落。" type="info" showIcon style={{ marginBottom: 16 }} />}
                {depCurfewRules.length === 0 ? (
                    <div className="curfew-rules__empty">没有设置起飞宵禁规则</div>
                ) : (
                    depCurfewRules.map((rule, index) => (
                        <div key={`dep-${index}`} className="curfew-rules__item">
                            <Space direction="vertical" style={{ width: "100%" }}>
                                {/* 规则类型选择 */}
                                <Radio.Group className="curfew-rules__type-selector" buttonStyle="solid" value={rule.type || "daily"} onChange={(e) => updateDepCurfewRuleType(index, e.target.value)}>
                                    <Radio.Button value="daily">每天</Radio.Button>
                                    <Radio.Button value="weekly">每周</Radio.Button>
                                    <Radio.Button value="specific">指定日期</Radio.Button>
                                </Radio.Group>

                                {/* 时间设置 */}
                                <Space>
                                    <Form.Item label="宵禁时段" required style={{ margin: 0 }}>
                                        <UTCTimeRangePicker className="curfew-rules__time-range-picker" format="HH:mm" value={formatTimeRangeValue(rule.start, rule.end)} onChange={(times) => updateDepCurfewRuleTimeRange(index, times)} />
                                    </Form.Item>

                                    {/* 根据类型显示不同的设置选项 */}
                                    {rule.type === "weekly" && (
                                        <Form.Item label="适用日期" style={{ margin: 0 }}>
                                            <Select mode="multiple" placeholder="选择星期几" style={{ width: 250 }} value={rule.weekdays || []} onChange={(weekdays) => updateDepCurfewRuleWeekdays(index, weekdays)} options={WEEKDAY_OPTIONS} />
                                        </Form.Item>
                                    )}

                                    {rule.type === "specific" && (
                                        <Form.Item label="日期范围 (UTC)" style={{ margin: 0 }}>
                                            <UTCDateRangePicker value={formatDateRangeValue(rule.date_start, rule.date_end)} onChange={(dates) => updateDepCurfewRuleDateRange(index, dates)} />
                                        </Form.Item>
                                    )}
                                </Space>

                                {/* 添加有效期范围设置（只对weekly类型显示） */}
                                {rule.type === "weekly" && (
                                    <Form.Item label="有效期范围 (UTC)" style={{ margin: 0 }}>
                                        <UTCDateRangePicker value={formatValidityRangeValue(rule.valid_from, rule.valid_to)} onChange={(dates) => updateDepCurfewRuleValidityRange(index, dates)} placeholder={["开始日期", "结束日期"]} />
                                        <div className="help-text" style={{ fontSize: "12px", color: "#8c8c8c", marginTop: "4px" }}>
                                            设置此宵禁规则的有效期范围，留空表示永久有效
                                        </div>
                                    </Form.Item>
                                )}

                                {/* 例外飞机设置 */}
                                <Form.Item label="例外飞机" style={{ margin: 0 }}>
                                    <AircraftSelector mode="multiple" placeholder="选择不受宵禁限制的飞机注册号" value={rule.exceptions || []} onChange={(exceptions) => updateDepCurfewRuleExceptions(index, exceptions)} valueType="registration" displayMode="detailed" />
                                </Form.Item>

                                {/* 删除按钮 */}
                                <div style={{ textAlign: "right" }}>
                                    <Button danger icon={<DeleteOutlined />} onClick={() => removeDepCurfewRule(index)}>
                                        删除规则
                                    </Button>
                                </div>
                            </Space>
                        </div>
                    ))
                )}
            </Card>

            <Divider style={{ margin: "24px 0" }} />

            {/* 降落宵禁规则 */}
            <Card
                title={
                    <span className="curfew-rules__title">
                        <ClockCircleOutlined style={{ color: "#fa8c16" }} />
                        降落宵禁规则
                        <Tooltip title="在设定的时间段内，所有飞机不允许在该机场降落，除非在例外列表中">
                            <InfoCircleOutlined />
                        </Tooltip>
                    </span>
                }
                extra={
                    <Button type="primary" size="small" icon={<PlusOutlined />} onClick={addArrCurfewRule}>
                        添加规则
                    </Button>
                }
                bordered={false}
                size="small"
            >
                {hasNonHourlyTimes(arrCurfewRules) && <Alert message="时间设置建议" description="检测到您设置的结束时间不是整点（例如 08:30 或 00:00）。建议使用整点时间（例如 08:00 或 23:00）以确保宵禁规则的清晰。系统允许在宵禁结束时点起飞或降落." type="info" showIcon style={{ marginBottom: 16 }} />}
                {arrCurfewRules.length === 0 ? (
                    <div className="curfew-rules__empty">没有设置降落宵禁规则</div>
                ) : (
                    arrCurfewRules.map((rule, index) => (
                        <div key={`arr-${index}`} className="curfew-rules__item">
                            <Space direction="vertical" style={{ width: "100%" }}>
                                {/* 规则类型选择 */}
                                <Radio.Group className="curfew-rules__type-selector" buttonStyle="solid" value={rule.type || "daily"} onChange={(e) => updateArrCurfewRuleType(index, e.target.value)}>
                                    <Radio.Button value="daily">每天</Radio.Button>
                                    <Radio.Button value="weekly">每周</Radio.Button>
                                    <Radio.Button value="specific">指定日期</Radio.Button>
                                </Radio.Group>

                                {/* 时间设置 */}
                                <Space>
                                    <Form.Item label="宵禁时段" required style={{ margin: 0 }}>
                                        <UTCTimeRangePicker className="curfew-rules__time-range-picker" format="HH:mm" value={formatTimeRangeValue(rule.start, rule.end)} onChange={(times) => updateArrCurfewRuleTimeRange(index, times)} />
                                    </Form.Item>

                                    {/* 根据类型显示不同的设置选项 */}
                                    {rule.type === "weekly" && (
                                        <Form.Item label="适用日期" style={{ margin: 0 }}>
                                            <Select mode="multiple" placeholder="选择星期几" style={{ width: 250 }} value={rule.weekdays || []} onChange={(weekdays) => updateArrCurfewRuleWeekdays(index, weekdays)} options={WEEKDAY_OPTIONS} />
                                        </Form.Item>
                                    )}

                                    {rule.type === "specific" && (
                                        <Form.Item label="日期范围 (UTC)" style={{ margin: 0 }}>
                                            <UTCDateRangePicker value={formatDateRangeValue(rule.date_start, rule.date_end)} onChange={(dates) => updateArrCurfewRuleDateRange(index, dates)} />
                                        </Form.Item>
                                    )}
                                </Space>

                                {/* 添加有效期范围设置（只对weekly类型显示） */}
                                {rule.type === "weekly" && (
                                    <Form.Item label="有效期范围 (UTC)" style={{ margin: 0 }}>
                                        <UTCDateRangePicker value={formatValidityRangeValue(rule.valid_from, rule.valid_to)} onChange={(dates) => updateArrCurfewRuleValidityRange(index, dates)} placeholder={["开始日期", "结束日期"]} />
                                        <div className="help-text" style={{ fontSize: "12px", color: "#8c8c8c", marginTop: "4px" }}>
                                            设置此宵禁规则的有效期范围，留空表示永久有效
                                        </div>
                                    </Form.Item>
                                )}

                                {/* 例外飞机设置 */}
                                <Form.Item label="例外飞机" style={{ margin: 0 }}>
                                    <AircraftSelector mode="multiple" placeholder="选择不受宵禁限制的飞机注册号" value={rule.exceptions || []} onChange={(exceptions) => updateArrCurfewRuleExceptions(index, exceptions)} valueType="registration" displayMode="simple" />
                                </Form.Item>

                                {/* 删除按钮 */}
                                <div style={{ textAlign: "right" }}>
                                    <Button danger icon={<DeleteOutlined />} onClick={() => removeArrCurfewRule(index)}>
                                        删除规则
                                    </Button>
                                </div>
                            </Space>
                        </div>
                    ))
                )}
            </Card>
        </div>
    );
};

export default CurfewRulesEditor;
